import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import { AnalysisResults } from '@sleipai/sleip-models';
import * as AnalysisActions from '../actions/analysis.actions';

export const analysisFeatureKey = 'analysis';

export interface State extends EntityState<Partial<AnalysisResults>> {
  loaded: boolean;
  loading: boolean;
  activeStride: number | null;
  analysisSharing: boolean;
  analysisSharedSuccessLink: string | null;
  analysisPDFGeneratedLink: string | null;
  analysisShareError: string | null;
}

export function selectDataId(a: Partial<AnalysisResults>) {
  return a?.id ?? '';
}

export function sortById(
  a: Partial<AnalysisResults>,
  b: Partial<AnalysisResults>
) {
  return a.id?.localeCompare(b.id ?? '') ?? -1;
}

export const adapter: EntityAdapter<Partial<AnalysisResults>> =
  createEntityAdapter<Partial<AnalysisResults>>({
    selectId: selectDataId,
    sortComparer: sortById,
  });

export const initialState: State = adapter.getInitialState({
  loading: false,
  loaded: false,
  activeStride: null,
  analysisSharing: false,
  analysisSharedSuccessLink: null,
  analysisPDFGeneratedLink: null,
  analysisShareError: null,
});

export const analysisReducer = createReducer(
  initialState,

  on(AnalysisActions.loadAnalysis, (state) => state),
  on(AnalysisActions.loadAnalysisSuccess, (state) => state),
  on(AnalysisActions.loadAnalysisFailure, (state) => state),
  on(AnalysisActions.loadAnalysisById, (state) => {
    return {
      ...state,
      loading: true,
    };
  }),
  on(AnalysisActions.addSingleAnalysis, (state, { analysis }) => {
    return adapter.upsertOne(analysis, {
      ...state,
      loading: false,
      loaded: true,
    });
  }),
  on(AnalysisActions.loadMultipleAnalysisResultsByIds, (state) => {
    return {
      ...state,
      loading: true,
    };
  }),
  on(
    AnalysisActions.loadMultipleAnalysisResultsByIdsSuccess,
    (state, { analysisResults: data }) => {
      return adapter.upsertMany(data, {
        ...state,
        loading: false,
        loaded: true,
      });
    }
  ),
  on(AnalysisActions.activeStride, (state, { id }) => {
    return {
      ...state,
      activeStride: id,
    };
  }),
  on(AnalysisActions.updateSingleAnalysis, (state, { update }) => {
    return adapter.updateOne(update, state);
  }),
  on(AnalysisActions.shareAnalysis, (state) => {
    return {
      ...state,
      analysisSharing: true,
      analysisShareError: null,
    };
  }),
  on(AnalysisActions.shareAnalysisSuccess, (state, { data }) => {
    return {
      ...state,
      analysisSharing: false,
      analysisSharedSuccessLink: data,
    };
  }),
  on(AnalysisActions.generateAnalysisResultPDF, (state) => {
    return {
      ...state,
      analysisSharing: true,
      analysisShareError: null,
    };
  }),
  on(
    AnalysisActions.generateAnalysisResultPDFSuccess,
    (state, { downloadUrl }) => {
      return {
        ...state,
        analysisSharing: false,
        analysisPDFGeneratedLink: downloadUrl,
      };
    }
  ),
  on(AnalysisActions.shareAnalysisFailure, (state, { error }) => {
    return {
      ...state,
      analysisSharing: false,
      analysisSharedSuccessLink: null,
      analysisPDFGeneratedLink: null,
      analysisShareError: error?.message ?? error ?? null,
    };
  }),
  on(
    AnalysisActions.resetShareAnalysisLink,
    AnalysisActions.cancelShareAnalysis,
    (state) => {
      return {
        ...state,
        analysisSharing: false,
        analysisSharedSuccessLink: null,
        analysisPDFGeneratedLink: null,
        analysisShareError: null,
      };
    }
  ),
  on(AnalysisActions.enableAnalysisLoadingFlag, (state) => {
    return {
      ...state,
      loading: true,
      loaded: false,
    };
  }),
  on(AnalysisActions.resetAnalysisLoadingFlag, (state) => {
    return {
      ...state,
      loading: false,
      loaded: true,
    };
  })
);

export function reducer(state: State | undefined, action: Action) {
  return analysisReducer(state, action);
}
